import { type PropsWithChildren, useCallback, useEffect, useState, forwardRef } from "react";
import {
  ApiProvider as CoreApiProvider,
  createApiInstance,
  type ApiContextType,
  type FormattedError,
} from "@humansignal/core";
import type { ApiResponse } from "@humansignal/core/lib/api-proxy/types";
import { ErrorWrapper } from "../components/Error/Error";
import { modal } from "../components/Modal/Modal";
import { API_CONFIG } from "../config/ApiConfig";
import { absoluteURL, isDefined } from "../utils/helpers";
import { FF_IMPROVE_GLOBAL_ERROR_MESSAGES, isFF } from "../utils/feature-flags";
import { ToastType, useToast } from "@humansignal/ui";
import {
  authHeaders,
  clearTokens,
  getRefreshToken,
  hasTokens,
  setTokens,
  tokensJustIssued,
} from "../utils/jwt-storage";
import { spaNavigate, currentNextParam } from "../utils/navigation";
import { OfflineModalHost, probeCloud, showOfflineModal } from "../components/OfflineCard/OfflineCard";
import { isCapacitor } from "../utils/host";
import { activeHostMode } from "../utils/hostMode";

export const IMPROVE_GLOBAL_ERROR_MESSAGES = isFF(FF_IMPROVE_GLOBAL_ERROR_MESSAGES);
// Duration for toast errors
export const API_ERROR_TOAST_DURATION = 10000;

/**
 * Capacitor mobile + JWT-flow web routes (/auth/login, /auth/signup)
 * authenticate via SimpleJWT and stash an access token in
 * localStorage. We seed the API instance's commonHeaders with the
 * Authorization Bearer header on startup so every API call carries
 * the JWT. Web users on session-cookie auth have no access token →
 * ``authHeaders()`` returns ``{}`` and the cookie path keeps working
 * unchanged. After login/logout the page reloads, this module is
 * re-evaluated, and the headers update.
 */
const initialHeaders = authHeaders();

/**
 * Silent access-token refresh (P3). The login mints a 24h access +
 * 30d refresh pair, but nothing ever CALLED /api/token/refresh/ —
 * so every session died at the 24h mark with a bounce to the login
 * screen. On the first 401 we now exchange the refresh token for a
 * fresh access token and let the app's own retry/refetch machinery
 * carry on; the login screen is reserved for a dead refresh token.
 *
 * Single-flight: concurrent 401s (a page load fires many queries)
 * share one refresh promise. A failed attempt sets a 30s cooldown so
 * a revoked token can't loop the endpoint.
 */
type RefreshOutcome = "ok" | "dead" | "transient";
let refreshInFlight: Promise<RefreshOutcome> | null = null;
let refreshFailedAt = 0;
const tryRefreshTokens = (): Promise<RefreshOutcome> => {
  if (refreshInFlight) return refreshInFlight;
  if (Date.now() - refreshFailedAt < 30_000) return Promise.resolve("transient");
  const refresh = getRefreshToken();
  if (!refresh) return Promise.resolve("dead");
  refreshInFlight = (async (): Promise<RefreshOutcome> => {
    try {
      const r = await fetch(absoluteURL("/api/token/refresh/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
        cache: "no-store",
      });
      if (r.status === 400 || r.status === 401 || r.status === 403) {
        // The SERVER rejected the refresh token — it's expired or
        // revoked. This is the one outcome that ends the session.
        console.warn("[ApiProvider] refresh token rejected →", r.status);
        return "dead";
      }
      if (!r.ok) {
        refreshFailedAt = Date.now();
        return "transient"; // 5xx — cloud hiccup, keep the session
      }
      const body = await r.json();
      if (!body?.access) {
        refreshFailedAt = Date.now();
        return "transient";
      }
      // SimpleJWT with ROTATE_REFRESH_TOKENS returns a new refresh
      // too; keep the old one when it doesn't.
      setTokens(body.access, body.refresh ?? undefined);
      refreshAuthHeaders();
      console.info("[ApiProvider] access token refreshed");
      return "ok";
    } catch (e) {
      // Network throw — offline or the wrapper's timeout. NEVER sign
      // out on this: an offline mobile session must survive (its
      // cached reads + outbox don't need a live access token).
      refreshFailedAt = Date.now();
      console.warn("[ApiProvider] token refresh unreachable", e);
      return "transient";
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

// Initialize API instance with Label Studio configuration
const apiInstance = createApiInstance({
  ...API_CONFIG,
  commonHeaders: {
    ...((API_CONFIG as { commonHeaders?: Record<string, string> }).commonHeaders ?? {}),
    ...initialHeaders,
  },
  onRequestFinished(res) {
    if (res.status === 401) {
      // Diagnostic — log the URL of every 401 that triggers the
      // auto-signout redirect. The user's "login worked then bounced
      // back to /auth/login" report is a classic symptom of one
      // background endpoint 401ing post-login and tripping this
      // handler; without the URL we're guessing. Keep the noise
      // scoped to actual redirect-triggering 401s (not, e.g., the
      // pre-login whoami 401 that legitimately gates the login flow).
      const url = (res as { url?: string }).url ?? "(unknown url)";
      console.warn("[ApiProvider] 401 →", url, "path:", location.pathname);
      // Already on the React auth surface (login / signup) — the user
      // is exactly where they need to be; don't yank them somewhere
      // else just because a background ``me`` query 401'd. Without
      // this guard the post-signout flow lands on the cloud's home
      // page instead of staying on /auth/login (the in-flight ``me``
      // query whose tokens we just cleared returns 401 → this handler
      // fires → navigates).
      if (location.pathname.startsWith("/auth/")) return;
      // Public receipt verification — anonymous by design; a shell
      // API 401 must not bounce the visitor to login.
      if (location.pathname.startsWith("/verify-receipt")) return;
      // Public host modes (an extension directory on its own host) treat
      // anonymous as a valid state: bouncing on every background 401
      // would reload the page in a loop. The shell shows its own
      // "Sign in" affordance instead.
      if (activeHostMode?.allowAnonymous) return;

      // Post-login race guard. ``setTokens`` writes localStorage but
      // the API instance's ``commonHeaders`` is patched via a
      // separate ``refreshAuthHeaders`` call; any query already
      // in-flight when the login POST resolves reaches the server
      // without a Bearer and comes back 401. Same-tab reload also
      // has a window between the new page loading its APIs (bundle
      // eval + boot script) and the FIRST authed request actually
      // going out. Signing the user out in that window undoes the
      // login they JUST completed. If the JWT was minted within the
      // last 8 seconds (per its ``iat`` claim), skip the signout
      // and let the next scheduled refetch use the patched headers.
      try {
        const t = window.localStorage?.getItem("afri:jwt:access");
        if (t) {
          const payload = t.split(".")[1];
          if (payload) {
            const claims = JSON.parse(
              atob(
                ((b) => {
                  while (b.length % 4) b += "=";
                  return b;
                })(payload.replace(/-/g, "+").replace(/_/g, "/")),
              ),
            );
            const iat = typeof claims?.iat === "number" ? claims.iat : null;
            const now = Math.floor(Date.now() / 1000);
            if (iat != null && now - iat < 8) {
              console.info("[ApiProvider] 401 within 8s of token issue — ignoring (login race)");
              return;
            }
          }
        }
      } catch {
        /* fall through to signout on any decode failure */
      }

      // JWT bundle: the access token is stale or rejected. Wipe and
      // bounce to the React login route so the user can re-auth.
      // Cookie-session web users have no JWT to clear; redirect to
      // ``/`` lets the existing Django ``user-login`` redirect chain
      // do its thing — same behaviour as before this hook existed.
      // ON CAPACITOR there is no Django at the WebView origin — ``/``
      // is the React shell, NOT a login page — so always send the
      // user to /auth/login regardless of token state.
      const isCapacitor =
        typeof window !== "undefined" &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof (window as any).Capacitor !== "undefined";
      if (hasTokens() && getRefreshToken()) {
        // Expired 24h access token with a live 30d refresh token: the
        // daily "bounced to login" annoyance. Refresh silently; the
        // queries that 401'd refetch on their own schedule with the
        // patched header. Only a FAILED refresh (revoked/expired
        // refresh token, or offline — the endpoint is deliberately
        // never stubbed) falls through to the sign-out below.
        void tryRefreshTokens().then((outcome) => {
          if (outcome !== "dead") return; // ok → refetches recover; transient → keep session
          // Freshly-minted tokens (<15s): even a "dead" verdict is
          // suspect — token propagation and clock skew can 401 the
          // first requests after login. Keep the session; the next
          // query retries with the same (valid) token.
          if (tokensJustIssued()) return;
          clearTokens();
          const next = currentNextParam();
          spaNavigate(next ? `/auth/login?next=${next}` : "/auth/login", true);
        });
        return;
      }
      if (hasTokens()) {
        if (tokensJustIssued()) return;
        clearTokens();
        const next = currentNextParam();
        spaNavigate(next ? `/auth/login?next=${next}` : "/auth/login", true);
      } else if (isCapacitor) {
        spaNavigate("/auth/login", true);
      } else if (location.pathname !== "/") {
        // Cookie-session web visitor on a deep link. Bouncing to ``/``
        // (Django ``main`` → ``user-login`` with no ``?next=``) used to
        // DROP the URL — an anonymous participant scanning a facilitator
        // invite QR (``/discover?invite=<token>``) lost the token at the
        // login wall. Go straight to the React login with ``?next=``
        // preserved instead; Login.tsx honours it after sign-in, and the
        // ``/auth/*`` early-return above prevents redirect loops.
        const next = currentNextParam();
        spaNavigate(next ? `/auth/login?next=${next}` : "/auth/login", true);
      } else {
        spaNavigate("/", true);
      }
    }
  },
});

// Export API instance for backward compatibility
export const API = apiInstance;

/**
 * Re-read the JWT from storage and patch it onto the live API
 * instance's ``commonHeaders``. The instance bakes its headers at
 * module-eval time (line 32 above) which predates login on the
 * Capacitor bundle, so without this nudge the post-login api
 * requests go out with no Authorization header and the cloud rejects
 * every one of them. Web/desktop don't need this because they hard-
 * reload after login and re-evaluate this module from scratch.
 */
export const refreshAuthHeaders = (): void => {
  const next = authHeaders();
  // Wipe any stale Authorization (logout case) before merging — using
  // ``Object.assign`` alone would leave a removed key in place.
  delete (apiInstance.commonHeaders as Record<string, string>).Authorization;
  Object.assign(apiInstance.commonHeaders, next);
};

// Re-export useAPI and ApiContext from core for convenience
export { useAPI, ApiContext } from "@humansignal/core";

export type ApiEndpoints = keyof typeof API.methods;

let apiLocked = false;

/**
 * Displays an error modal with the error details.
 */
const displayErrorModal = (errorDetails: FormattedError) => {
  const { isShutdown, title, message, stacktrace, ...formattedError } = errorDetails;

  modal({
    unique: "network-error",
    allowClose: !isShutdown,
    body: isShutdown ? (
      <ErrorWrapper
        possum={false}
        title={"Connection refused"}
        message={"Server not responding. Is it still running?"}
      />
    ) : (
      <ErrorWrapper
        {...formattedError}
        title={title}
        message={message}
        stacktrace={IMPROVE_GLOBAL_ERROR_MESSAGES ? undefined : stacktrace}
      />
    ),
    simple: true,
    style: { width: 680 },
  });
};

/**
 * Label Studio application-specific ApiProvider.
 * Wraps the core ApiProvider with Label Studio-specific error handling.
 */
export const ApiProvider = forwardRef<ApiContextType, PropsWithChildren<Record<string, never>>>(({ children }, ref) => {
  const toast = useToast();

  /**
   * Handles errors with Label Studio-specific logic including:
   * - Toast notifications for 4xx errors
   * - Modal errors for validation errors
   * - Console logging for server errors (no external telemetry yet)
   */
  const handleError = useCallback(
    (errorDetails: FormattedError, result: ApiResponse) => {
      const status = result.$meta?.status;
      const is4xx = status?.toString().startsWith("4");
      const containsValidationErrors =
        isDefined(result.response?.validation_errors) && Object.keys(result.response.validation_errors).length > 0;

      // 403 with ``error_code: "project_unavailable"`` — a task-level
      // write endpoint (submit / skip / review / draft / update)
      // telling us the project got paused, deleted, or the user lost
      // access mid-task. Redirect to /projects with a friendly toast
      // instead of the generic "Annotation is not saved" error the
      // editor surfaces otherwise.
      const errorCode = (result.response as any)?.error_code ?? (result.response as any)?.detail?.error_code;
      if (status === 403 && errorCode === "project_unavailable" && !apiLocked) {
        apiLocked = true;
        sessionStorage.setItem(
          "redirectMessage",
          "This project is not available. It may have been paused, deleted, or you were removed from it.",
        );
        location.href = absoluteURL("/projects");
        return;
      }

      // 404 on a project-scoped endpoint means the user either lost
      // access (project paused, removed from org, role downgraded) or
      // the project was deleted. Redirect them to /projects with a
      // friendly toast instead of surfacing Django's raw "No Project
      // matches the given query" string. Scoped to project URLs so
      // generic 404s elsewhere still fall through to the normal
      // error paths.
      if (status === 404 && !apiLocked) {
        const onProject =
          location.pathname.startsWith("/projects/") ||
          /\/api\/projects\/\d+/.test(result.$meta?.url ?? "") ||
          /\/api\/dm\//.test(result.$meta?.url ?? "");
        if (onProject) {
          apiLocked = true;
          sessionStorage.setItem(
            "redirectMessage",
            "That project isn't available any more. It may have been paused, deleted, or you were removed from it.",
          );
          location.href = absoluteURL("/projects");
          return;
        }
      }

      // 401 — fall through to handleFatalError below. Avoid doubling
      // up error toasts since handleFatalError already redirects.
      if (status === 401) {
        return;
      }

      // Desktop offline mode: when the local proxy can't reach Django,
      // it returns 503 with ``{"error": "upstream unreachable: ..."}``.
      // Don't surface the generic "Runtime error / Service Unavailable"
      // modal for these — the SPA can't recover from a single API call,
      // but the rest of the cached UI is fine. The user trips the proxy's
      // own offline / unlock UI on the next top-level navigation.
      const responseError = (result.response as any)?.error;
      const responseCode = (result.response as any)?.code;
      if (
        status === 503 &&
        (responseCode === "offline" ||
          (typeof responseError === "string" &&
            (responseError.includes("upstream unreachable") ||
              responseError.includes("unreachable") ||
              responseError.includes("online-only"))))
      ) {
        // Desktop offline: the cloud-proxy middleware answers 503 with
        // ``code: "offline"`` (or an "…unreachable / online-only"
        // message) for actions that need the cloud — Discover join,
        // settings saves, contributor rosters. One global offline
        // modal explains it; the raw 'Service Unavailable' used to
        // leak into whatever dialog made the call.
        showOfflineModal({ kind: "action" });
        return;
      }

      // WEB build with the cloud gone (502/504 from a proxy in front
      // of a dead Django, or a bare 503): the browser app is
      // online-only, so no single-call recovery exists. Show the
      // offline modal (auto-retrying; reloads when the cloud answers)
      // instead of the cryptic "Runtime error / Gateway Time-out".
      // Desktop (:17080) and Capacitor have their own offline UX.
      const isDesktopShell =
        (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
        window.location.port === "17080";
      // Capacitor: the bundle talks to the cloud (or, in dev, to a proxy
      // that can briefly 502/504 while the backend restarts). A blocking
      // "Runtime error / Gateway Time-out" modal for one failed call is
      // wrong — the offline pill + request retries already cover the
      // real-outage case. One deduped toast, no modal.
      if ((status === 502 || status === 503 || status === 504) && isCapacitor()) {
        toast?.show({
          id: "gateway-transient",
          message: "The server did not answer in time. Retrying in the background.",
          type: ToastType.info,
          duration: 5000,
        });
        return;
      }

      if ((status === 502 || status === 503 || status === 504) && !isDesktopShell && !isCapacitor()) {
        // Don't block the whole screen on a SINGLE 5xx — the server runs
        // nginx in front of gunicorn, so a lone instance recycling (or a
        // cold start) makes nginx briefly answer 502/503 while Django is
        // unreachable on :8000, even though the cloud is otherwise fine.
        // Confirm with one authoritative /api/version/ probe: if it says
        // reachable, this was a transient blip — swallow it. Only a probe
        // that ALSO fails means a real outage worth the modal. The modal,
        // once up, keeps re-probing and reloads when the cloud answers.
        probeCloud().then((reachable) => {
          if (!reachable) showOfflineModal({ kind: "web" });
        });
        return;
      }

      // Log server errors to the console for visibility during dev.
      // Upstream shipped these to Sentry; AfriAnnotate doesn't wire a
      // telemetry backend, so the browser console is the runtime log.
      if ((!is4xx || result.response?.exc_info) && result.error) {
        console.error("[API error]", result.error, {
          status,
          server_stacktrace: result.response?.exc_info,
          server_version: result.response?.version,
        });
      }

      // Show toast for 4xx without validation errors
      if (IMPROVE_GLOBAL_ERROR_MESSAGES && is4xx && !containsValidationErrors) {
        toast?.show({
          message: `${errorDetails.title}: ${errorDetails.message}`,
          type: ToastType.error,
          duration: API_ERROR_TOAST_DURATION,
        });
      } else {
        // Show modal for validation errors or non-4xx
        displayErrorModal(errorDetails);
      }
    },
    [toast],
  );

  /**
   * Handles fatal errors like 401 and 404.
   */
  const handleFatalError = useCallback((errorDetails: FormattedError, result: ApiResponse) => {
    if (apiLocked) return;

    const status = result.$meta?.status;

    // Handle 401 redirects
    if (status === 401) {
      apiLocked = true;
      if (activeHostMode?.allowAnonymous) return;
      location.href = absoluteURL("/");
      return;
    }

    // 404 on any resource → redirect home with a friendly toast instead of
    // surfacing Django's raw "No Project matches the given query" to the user.
    if (status === 404) {
      apiLocked = true;
      const redirectUrl = location.pathname.startsWith("/projects") ? absoluteURL("/projects") : absoluteURL("/");

      sessionStorage.setItem("redirectMessage", "The page or resource you were looking for does not exist.");
      location.href = redirectUrl;
    }
  }, []);

  // Check for redirect messages on mount
  useEffect(() => {
    const redirectMessage = sessionStorage.getItem("redirectMessage");
    if (redirectMessage) {
      toast?.show({
        message: redirectMessage,
        type: ToastType.error,
        duration: API_ERROR_TOAST_DURATION,
      });
      sessionStorage.removeItem("redirectMessage");
    }
  }, [toast]);

  // Belt-and-suspenders: call refreshAuthHeaders on every mount so
  // apiInstance.commonHeaders is guaranteed to reflect the current
  // localStorage JWT before the first user-scoped query fires.
  // ``initialHeaders = authHeaders()`` at module load time reads
  // localStorage ONCE — if the module happened to load a beat
  // before setTokens (via any code path — signup finishing an
  // in-flight request, JWT restored from an offline outbox drain,
  // etc), the commonHeaders would be empty and every subsequent
  // query would 401 without a Bearer, tripping the signout handler
  // above. Calling it here on mount closes that window at zero
  // cost — refreshAuthHeaders is a couple of ``Object.assign``s.
  useEffect(() => {
    refreshAuthHeaders();
  }, []);

  return (
    <CoreApiProvider ref={ref} onError={handleError} onFatalError={handleFatalError}>
      {children}
      <OfflineModalHost />
    </CoreApiProvider>
  );
});

ApiProvider.displayName = "ApiProvider";
